import { useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { dialog } from "../../components/ui/Dialog";
import {
  basculerFavoriApi, changerStatutApi, chargerProcedures, chargerVersionsApi, corbeilleApi, creerPageApi,
  creerSectionApi, dupliquerPageApi, enregistrerPageApi, modifierAclApi, restaurerVersionApi,
} from "./procApi";

// Stockage du manuel de procédures, branché sur l'API (base partagée).
//
// L'écran ne parle qu'à ce hook ([db, api, etat]) : db a la même forme qu'avant
// (spaces, sections, pages, log, favs). Le serveur vérifie les droits et envoie
// la page à jour avec les lignes de journal créées ; on ne fait que les fusionner.
// Seule la dernière version d'une page arrive avec son contenu : l'historique
// complet se charge à l'ouverture (api.loadVersions).

// Dernier état chargé : réaffiché tout de suite en revenant sur l'écran.
let cacheProc = null;

// Le HTML des pages est partagé entre utilisateurs : il est assaini avant tout
// usage (affichage, éditeur, analyse), jamais pris tel quel.
// Les liens des procédures s'ouvrent dans un nouvel onglet (target="_blank") :
// DOMPurify retire cet attribut par défaut, on le conserve en forçant rel.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target")) node.setAttribute("rel", "noopener noreferrer");
});
const sain = (html) => (html ? DOMPurify.sanitize(html, { ADD_ATTR: ["target"] }) : html);
const assainirPage = (p) => ({ ...p, versions: p.versions.map((v) => ({ ...v, html: sain(v.html) })) });

// Garde le contenu des anciennes versions déjà chargées quand la réponse n'en
// porte pas (seule la dernière version est envoyée avec son contenu).
const fusionPage = (ancienne, nouvelle) => {
  const p = assainirPage(nouvelle);
  if (!ancienne) return p;
  const connu = new Map(ancienne.versions.map((v) => [v.v, v.html]));
  return { ...p, versions: p.versions.map((v) => (v.html ? v : { ...v, html: connu.get(v.v) || "" })) };
};

export function useProcStore() {
  const [db, setDb] = useState(() => cacheProc || { spaces: [], sections: [], pages: [], log: [], favs: [] });
  const [chargement, setChargement] = useState(!cacheProc);
  const [erreur, setErreur] = useState(null);

  const maj = (fn) =>
    setDb((d) => {
      const suivant = fn(d);
      cacheProc = suivant;
      return suivant;
    });

  const recharger = useCallback(async () => {
    try {
      const data = await chargerProcedures();
      const suivant = {
        spaces: data.espaces,
        sections: data.sections,
        pages: data.pages.map(assainirPage),
        log: data.journal,
        favs: data.favoris,
      };
      cacheProc = suivant;
      setDb(suivant);
      setErreur(null);
    } catch (e) {
      console.error("Procédures — chargement :", e);
      setErreur(e.message || "Chargement impossible.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    recharger();
  }, [recharger]);

  const appliquerPage = ({ page, evenements }) =>
    maj((d) => ({
      ...d,
      pages: d.pages.some((p) => p.id === page.id)
        ? d.pages.map((p) => (p.id === page.id ? fusionPage(p, page) : p))
        : [...d.pages, fusionPage(null, page)],
      log: [...(evenements || []), ...d.log],
    }));

  // Une action refusée ou en échec est signalée sans casser l'écran.
  const executer = async (fn) => {
    try {
      return await fn();
    } catch (e) {
      console.error("Procédures :", e);
      await dialog.alert(e.message || "L'opération a échoué.");
      return null;
    }
  };

  const api = {
    async create({ title, spaceId, sectionId, html, origine }) {
      const r = await executer(() => creerPageApi({ title, spaceId, sectionId, html: sain(html), origine }));
      if (!r) return null;
      appliquerPage(r);
      return r.page.id;
    },

    async save(id, { title, html, summary, meta }) {
      const r = await executer(() => enregistrerPageApi(id, { title, html: sain(html), summary, meta }));
      if (r) appliquerPage(r);
      return !!r;
    },

    // Charge le contenu de toutes les versions (comparaison, restauration).
    async loadVersions(id) {
      const versions = await executer(() => chargerVersionsApi(id));
      if (!versions) return false;
      maj((d) => ({
        ...d,
        pages: d.pages.map((p) => (p.id === id ? { ...p, versions: versions.map((v) => ({ ...v, html: sain(v.html) })) } : p)),
      }));
      return true;
    },

    async restore(id, v) {
      const r = await executer(() => restaurerVersionApi(id, v));
      if (r) appliquerPage(r);
      return !!r;
    },

    async setStatus(id, status) {
      const r = await executer(() => changerStatutApi(id, status));
      if (r) appliquerPage(r);
      return !!r;
    },

    async trash(id, on = true) {
      const r = await executer(() => corbeilleApi(id, on));
      if (r) appliquerPage(r);
      return !!r;
    },

    async duplicate(id) {
      const r = await executer(() => dupliquerPageApi(id));
      if (!r) return null;
      appliquerPage(r);
      return r.page.id;
    },

    async setAcl(kind, id, acl) {
      const r = await executer(() => modifierAclApi({ kind, id, acl }));
      if (!r) return false;
      if (kind === "space") {
        maj((d) => ({ ...d, spaces: d.spaces.map((s) => (s.id === id ? r.espace : s)), log: [...r.evenements, ...d.log] }));
      } else {
        appliquerPage(r);
      }
      return true;
    },

    async toggleFav(id) {
      const etait = db.favs.includes(id);
      maj((d) => ({ ...d, favs: etait ? d.favs.filter((x) => x !== id) : [...d.favs, id] }));
      try {
        await basculerFavoriApi(id);
      } catch (e) {
        maj((d) => ({ ...d, favs: etait ? [...d.favs, id] : d.favs.filter((x) => x !== id) }));
        await dialog.alert(e.message || "Le favori n'a pas pu être enregistré.");
      }
    },

    async addSection(spaceId, name) {
      const r = await executer(() => creerSectionApi(spaceId, name));
      if (!r) return null;
      maj((d) => ({ ...d, sections: [...d.sections, r.section], log: [...r.evenements, ...d.log] }));
      return r.section.id;
    },
  };

  return [db, api, { chargement, erreur, recharger }];
}
